import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import './floatingContactsMenu.scss';

type FloatingContactsMenuProps = {
    telegramHref: string;
    maxHref?: string;
    emailHref: string;
    className?: string;
};

const FloatingContactsMenu: React.FC<FloatingContactsMenuProps> = ({ telegramHref, maxHref, emailHref, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const actionsId = useId();

    const close = useCallback(() => setIsOpen(false), []);

    const toggle = useCallback(() => {
        setIsOpen((v) => !v);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!isOpen) return;
        const onPointerDown = (e: PointerEvent) => {
            const root = rootRef.current;
            if (!root) return;
            const target = e.target as Node | null;
            if (!target) return;
            if (root.contains(target)) return;
            close();
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, close]);

    const onActionClick = useCallback(() => {
        close();
    }, [close]);

    return (
        <div className="footerFloatingContainer">
            <div ref={rootRef} className={['footerFloatingContacts', isOpen ? 'footerFloatingContacts--open' : '', className ? className : ''].join(' ')}>
                <div className="footerFloatingContacts__actions" id={actionsId} aria-hidden={!isOpen}>
                    <a
                        className="footerFloatingContacts__action footerFloatingContacts__action--telegram"
                        href={telegramHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Открыть Telegram"
                        onClick={onActionClick}
                    >
                        <img
                            className="footerFloatingContacts__icon footerFloatingContacts__icon--png"
                            src="/icons/new/feedback_telegram.png"
                            alt=""
                            draggable={false}
                        />
                    </a>

                    {maxHref && (
                        <a
                            className="footerFloatingContacts__action footerFloatingContacts__action--max"
                            href={maxHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Открыть Max"
                            onClick={onActionClick}
                        >
                            <img className="footerFloatingContacts__icon" src="/icons/new/feedback_max.svg" alt="" draggable={false} />
                        </a>
                    )}

                    <a
                        className="footerFloatingContacts__action footerFloatingContacts__action--email"
                        href={emailHref}
                        aria-label="Написать на почту"
                        onClick={onActionClick}
                    >
                        <img className="footerFloatingContacts__icon" src="/icons/new/feedback_email.svg" alt="" draggable={false} />
                    </a>
                </div>

                <button
                    type="button"
                    className="footerFloatingContacts__toggle"
                    aria-label={isOpen ? 'Закрыть меню контактов' : 'Открыть меню контактов'}
                    aria-expanded={isOpen}
                    aria-controls={actionsId}
                    onClick={toggle}
                >
                    <span className="footerFloatingContacts__toggleIcon" aria-hidden="true">
                        <img
                            className="footerFloatingContacts__toggleImg footerFloatingContacts__toggleImg--open"
                            src="/icons/new/feedback_close.svg"
                            alt=""
                            draggable={false}
                        />
                        <img
                            className="footerFloatingContacts__toggleImg footerFloatingContacts__toggleImg--closed"
                            src="/icons/new/feedback.svg"
                            alt=""
                            draggable={false}
                        />
                    </span>
                </button>
            </div>
        </div>
    );
};

export default FloatingContactsMenu;
